"use client";

import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { Play } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  videoUrl?: string | null;
}

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, videoUrl, ...props }, ref) => {
  const { t } = useTranslation(["common"]);
  const [isPlayerOpen, setIsPlayerOpen] = React.useState(false);
  const [isVideoLoading, setIsVideoLoading] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);

  const openVideoOverlay = () => {
    if (!videoUrl) return;
    setIsPlayerOpen(true);
    setIsVideoLoading(true);
  };

  const closeVideoOverlay = () => {
    setIsPlayerOpen(false);
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {
        // ignore
      }
    }
  };

  return (
    <>
      <AvatarPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full group",
          className
        )}
        {...props}
      >
        {props.children}
        {videoUrl && (
          <div
            onClick={openVideoOverlay}
            className="absolute inset-0 bg-black/20 group-hover:bg-black/40 flex items-center justify-center transition-all cursor-pointer z-10"
            title={t("common:buttons.play")}
          >
            <Play className="w-1/3 h-1/3 text-white/80 group-hover:text-white fill-white/80 group-hover:fill-white transition-colors" />
          </div>
        )}
      </AvatarPrimitive.Root>

      {isPlayerOpen && videoUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
          <button
            type="button"
            aria-label={t("common:buttons.close")}
            className="absolute top-4 right-4 text-white/80 hover:text-white text-sm bg-white/10 hover:bg-white/20 rounded px-3 py-1 z-[60]"
            onClick={(e) => {
              e.stopPropagation();
              closeVideoOverlay();
            }}
          >
            {t("common:buttons.close")}
          </button>
          <div
            className="w-full max-w-5xl px-4"
            onClick={(e) => e.stopPropagation()}
          >
            {isVideoLoading && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-white/80 z-[-1]">
                {t("common:loading_video", {
                  defaultValue: t("common:status.loading")
                })}
              </div>
            )}
            <video
              ref={videoRef}
              className="w-full h-auto aspect-video bg-black relative z-10"
              controls
              src={videoUrl}
              onLoadedData={() => setIsVideoLoading(false)}
              onError={() => setIsVideoLoading(false)}
              autoPlay
            />
          </div>
        </div>
      )}
    </>
  );
});
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
